import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils';

export type ToggleChipSize = 'default' | 'sm';
export type ToggleChipTone = 'blue' | 'teal' | 'amber' | 'pink' | 'navy';

export interface ToggleChipStyleOptions {
  checked?: boolean;
  size?: ToggleChipSize;
  tone?: ToggleChipTone;
  locked?: boolean;
  disabled?: boolean;
}

/**
 * One record per ink. `checked` is the filled state, `hover` the tint an
 * unselected chip picks up under the cursor, `tick` the colour of the check
 * glyph once the box turns white, `suffix` the micro-badge when unselected.
 */
const TONES: Record<
  ToggleChipTone,
  { checked: string; hover: string; ring: string; tick: string; suffix: string }
> = {
  blue: {
    checked: 'border-riso-blue bg-riso-blue text-white shadow-hard-blue',
    hover: 'hover:border-riso-blue hover:bg-riso-blue-light',
    ring: 'focus-visible:ring-riso-blue',
    tick: 'text-riso-blue',
    suffix: 'bg-riso-blue-light text-riso-blue',
  },
  teal: {
    checked: 'border-riso-teal bg-riso-teal text-white shadow-hard-teal',
    hover: 'hover:border-riso-teal hover:bg-riso-teal-light',
    ring: 'focus-visible:ring-riso-teal',
    tick: 'text-riso-teal',
    suffix: 'bg-riso-teal-light text-riso-teal',
  },
  amber: {
    checked: 'border-riso-amber bg-riso-amber text-white shadow-hard-amber',
    hover: 'hover:border-riso-amber hover:bg-riso-amber-light',
    ring: 'focus-visible:ring-riso-amber',
    tick: 'text-riso-amber',
    suffix: 'bg-riso-amber-light text-riso-amber',
  },
  pink: {
    checked: 'border-riso-pink bg-riso-pink text-white shadow-hard-pink',
    hover: 'hover:border-riso-pink hover:bg-riso-pink-light',
    ring: 'focus-visible:ring-riso-pink',
    tick: 'text-riso-pink',
    suffix: 'bg-riso-pink-light text-riso-pink',
  },
  navy: {
    checked: 'border-primary bg-primary text-primary-foreground shadow-hard',
    hover: 'hover:border-primary hover:bg-secondary',
    ring: 'focus-visible:ring-ring',
    tick: 'text-primary',
    suffix: 'bg-secondary text-primary',
  },
};

const SIZES: Record<
  ToggleChipSize,
  { chip: string; tick: string; check: string; icon: string }
> = {
  default: {
    chip: 'h-11 gap-2.5 px-4 text-sm',
    tick: 'h-[18px] w-[18px]',
    check: 'h-3 w-3',
    icon: '[&_svg]:h-4 [&_svg]:w-4',
  },
  sm: {
    chip: 'h-[34px] gap-2 px-3 text-xs',
    tick: 'h-[15px] w-[15px]',
    check: 'h-2.5 w-2.5',
    icon: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  },
};

/**
 * The chip shell. Exported on its own so non-React callers — the Astro pricing
 * section, which drives its chips from an inline script — can render the exact
 * same look without pulling in React.
 */
export function toggleChipClasses({
  checked = false,
  size = 'default',
  tone = 'blue',
  locked = false,
  disabled = false,
}: ToggleChipStyleOptions = {}): string {
  const ink = TONES[tone] ?? TONES.blue;
  const scale = SIZES[size] ?? SIZES.default;
  // Locked and disabled both stop reacting to the cursor; only disabled fades.
  const frozen = locked || disabled;

  return cn(
    'group inline-flex items-center justify-center whitespace-nowrap rounded-full border-2 font-display outline-none transition-all duration-200 ease-spring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    scale.chip,
    ink.ring,
    // One weight in both states: bolding on select would widen the chip.
    'font-semibold',
    checked ? ink.checked : 'border-border bg-card text-foreground',
    // A selected chip already carries its ink, so hover only tints an off one.
    !frozen && !checked && cn('hover:shadow-hard-sm', ink.hover),
    disabled && 'cursor-not-allowed opacity-50',
    !disabled && (locked ? 'cursor-default' : 'cursor-pointer'),
  );
}

/** The tick box, rendered in both states so the chip never changes width. */
export function toggleChipTickClasses({
  checked = false,
  size = 'default',
  tone = 'blue',
  locked = false,
  disabled = false,
}: ToggleChipStyleOptions = {}): string {
  const ink = TONES[tone] ?? TONES.blue;
  const scale = SIZES[size] ?? SIZES.default;
  const frozen = locked || disabled;

  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-sm border-2 transition-all duration-200 ease-spring',
    scale.tick,
    ink.tick,
    checked
      ? 'border-white bg-white'
      : cn('border-border bg-transparent', !frozen && ink.hover),
  );
}

/** The leading icon slot — sizes whatever svg it is handed. */
export function toggleChipIconClasses({
  checked = false,
  size = 'default',
}: ToggleChipStyleOptions = {}): string {
  const scale = SIZES[size] ?? SIZES.default;

  return cn(
    'flex shrink-0',
    scale.icon,
    checked ? 'text-current opacity-90' : 'text-muted-foreground',
  );
}

/** The trailing micro-badge — a price delta, a count. */
export function toggleChipSuffixClasses({
  checked = false,
  tone = 'blue',
}: ToggleChipStyleOptions = {}): string {
  const ink = TONES[tone] ?? TONES.blue;

  return cn(
    '-mr-1.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.08em]',
    checked ? 'bg-white/20 text-current' : ink.suffix,
  );
}

export interface ToggleChipProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'children'
> {
  children?: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Rendered before the label. */
  icon?: React.ReactNode;
  /** Trailing micro-badge — a price delta, a count. */
  suffix?: React.ReactNode;
  /** @default "default" — 44px tall; `sm` is 34px. */
  size?: ToggleChipSize;
  /** Selected ink. @default "blue" */
  tone?: ToggleChipTone;
  /**
   * Checked and not removable (e.g. the last remaining module). Reads enabled —
   * full contrast, no fade — but ignores clicks. Never use `disabled` for this:
   * it reads as unavailable rather than as required.
   */
  locked?: boolean;
}

/**
 * Multi-select chip — a checkbox with the presence of a button. Use it for a
 * row of independent on/off choices that must stay readable at a glance:
 * modules in a pricing selector, filters above a table, tags on a client.
 * State lives per chip, not in the group.
 *
 * Mutually exclusive options belong in `SegmentedControl`; a checkbox in a form
 * row belongs in `Checkbox`; more than ~6 options belong in `MultiSelect`.
 */
const ToggleChip = React.forwardRef<HTMLButtonElement, ToggleChipProps>(
  (
    {
      className,
      children,
      checked = false,
      onCheckedChange,
      icon,
      suffix,
      size = 'default',
      tone = 'blue',
      locked = false,
      disabled = false,
      onClick,
      ...props
    },
    ref,
  ) => {
    const frozen = locked || disabled;

    return (
      <button
        ref={ref}
        {...props}
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-disabled={frozen || undefined}
        disabled={disabled}
        data-state={checked ? 'checked' : 'unchecked'}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented || frozen) return;
          onCheckedChange?.(!checked);
        }}
        className={cn(
          toggleChipClasses({ checked, size, tone, locked, disabled }),
          className,
        )}
      >
        <span
          aria-hidden="true"
          className={toggleChipTickClasses({
            checked,
            size,
            tone,
            locked,
            disabled,
          })}
        >
          {checked && (
            <Check
              className={cn('animate-check-pop', SIZES[size].check)}
              strokeWidth={3}
            />
          )}
        </span>
        {icon && (
          <span
            aria-hidden="true"
            className={toggleChipIconClasses({ checked, size })}
          >
            {icon}
          </span>
        )}
        {children != null && <span>{children}</span>}
        {suffix != null && suffix !== '' && (
          <span className={toggleChipSuffixClasses({ checked, tone })}>
            {suffix}
          </span>
        )}
      </button>
    );
  },
);
ToggleChip.displayName = 'ToggleChip';

export { ToggleChip };
